/**
 * @file  functions/index
 * @description  function wrapper for the http calls and option settings
 *
 * @author ogarcia (ozkary)
 *
 */
import { getAuthToken } from '../functions/auth/index';
import { error } from '../services/logger';
/**
 * defines the http request options
 */
const requestOptions: {
  method: string;
  headers: HeadersInit;
  body?: string | null;
} = {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: ''
};

/**
 * gets a request option for a call
 * @param method GET/POST/PUT
 * @param body json string with the payload, empty for GET
 */
export const getRequestOptions = async (
  method: string,
  body?: string | null,
  useToken = true
): Promise<RequestInit> => {
  requestOptions.method = method;
  if (body) {
    requestOptions.body = body;
  } else {
    requestOptions.body = null;
  }

  // add the custom token
  const token = await getAuthToken(useToken);
  const headers = new Headers(requestOptions.headers);
  if (token && token.name && token.value) {
    headers.set(token.name, token.value);
  }
  requestOptions.headers = headers;

  return requestOptions;
};

/**
 * sends a get request with the corresponding header information
 * @param route the uri route for the request
 * @returns T data JSON
 */
export const get = async function <T>(route: string): Promise<T> {
  const options = await getRequestOptions('GET');
  let data: T = {};
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API}/${route}`,
      options
    );
    data = await response.json();
  } catch (err) {
    error(err);
  }
  return data;
};

/**
 * gets a header value from a request
 * @param route the api route
 * @param key the header name
 * @returns the header value
 */
export const getHeader = async function (
  route: string,
  key: string
): Promise<string> {
  const useToken = route?.toLowerCase() !== 'signin';
  const options = await getRequestOptions('GET', null, useToken);
  let value = '';
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API}/${route}`,
      options
    );

    for (const header of response.headers.entries()) {
      if (header[0] === key?.toLowerCase()) {
        value = header[1];
        break;
      }
    }
  } catch (err) {
    error(err);
  }

  return value;
};

/**
 * sends a post request with a json payload
 * @param route the uri route for the request
 * @param payload the json data
 * @returns T data JSON
 */
export const post = async function <T>(route: string, payload: T): Promise<T> {
  const options = await getRequestOptions('POST', JSON.stringify(payload));
  const response = await fetch(
    `${process.env.REACT_APP_API}/${route}`,
    options
  );
  const data: T = await response.json();
  return data;
};

/**
 * Sends a POSt request and returns a JSON with different type
 * @param route the url api route
 * @param payload the json data
 * @returns R data in JSON
 */
export const submit = async function <T, R>(
  route: string,
  payload: T
): Promise<R> {
  const options = await getRequestOptions('POST', JSON.stringify(payload));
  const response = await fetch(
    `${process.env.REACT_APP_API}/${route}`,
    options
  );
  const data: R = await response.json();
  return data;
};

/**
 * sends a post request to a remote/external site
 * @param route external url
 * @param payload the payload to send
 * @param option header
 * @returns the external endpoint response
 */
export const postExternal = async function <T, R>(
  route: string,
  payload: T,
  header?: HeadersInit
): Promise<R> {
  const options = await getRequestOptions('POST', JSON.stringify(payload));

  // add any additional header
  if (header) {
    options.headers = { ...options.headers, ...header };
  }

  const response = await fetch(route, options);
  const data: R = await response.json();
  return data;
};

/**
 * sends a put request with a json payload
 * @param route the uri route for the request
 * @param payload the json data
 * @returns T data JSON
 */
export const put = async function <T>(route: string, payload: T): Promise<T> {
  const options = await getRequestOptions('PUT', JSON.stringify(payload));
  const response = await fetch(
    `${process.env.REACT_APP_API}/${route}`,
    options
  );
  const data: T = await response.json();
  return data;
};
